if (!customElements.get('eh-home-slider')) {
  customElements.define('eh-home-slider', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      clearTimeout(this.timer);
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.index = 0;
      this.videos = [...this.querySelectorAll('video')];
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.duration = Math.min(10000, Math.max(3000, Number(this.dataset.duration) || 5000));
      this.paused = this.motion.matches;
      this.visible = false;
      this.focused = false;
      this.hovered = false;
      this.editorSelected = false;
      this.toggle = this.querySelector('[data-slideshow-toggle]');
      this.motion.addEventListener('change', event => {
        this.paused = event.matches;
        this.updatePlayback();
      }, options);
      document.addEventListener('visibilitychange', () => this.updatePlayback(), options);
      this.intersectionObserver?.disconnect();
      this.intersectionObserver = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.updatePlayback();
      });
      this.intersectionObserver.observe(this);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'touch') return;
        this.hovered = true;
        this.updatePlayback();
      }, options);
      this.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.updatePlayback();
      }, options);
      this.addEventListener('focusin', () => {
        this.focused = true;
        this.updatePlayback();
      }, options);
      this.addEventListener('focusout', () => queueMicrotask(() => {
        this.focused = this.contains(document.activeElement);
        this.updatePlayback();
      }), options);
      this.querySelectorAll('[data-direction]').forEach(button => {
        button.addEventListener('click', () => this.show(this.index + Number(button.dataset.direction)), options);
      });
      this.toggle?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.updatePlayback();
      }, options);
      this.addEventListener('shopify:block:select', event => {
        this.editorSelected = true;
        const index = this.slides.findIndex(slide => slide === event.target || slide.contains(event.target));
        if (index >= 0) this.show(index);
      }, options);
      this.addEventListener('shopify:block:deselect', () => {
        this.editorSelected = false;
        this.updatePlayback();
      }, options);
      this.show(0);
    }
    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.timer);
      this.intersectionObserver?.disconnect();
      this.videos?.forEach(video => video.pause());
    }
    show(index) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; });
      this.updatePlayback();
    }
    updatePlayback() {
      clearTimeout(this.timer);
      const rotating = this.autoplay && !this.paused && !this.focused && !this.hovered && !this.editorSelected && this.visible && !document.hidden;
      if (rotating) this.timer = setTimeout(() => this.show(this.index + 1), this.duration);
      this.querySelector('.eh-slides')?.setAttribute('aria-live', rotating ? 'off' : 'polite');
      if (this.toggle) {
        this.toggle.setAttribute('aria-pressed', String(this.paused));
        this.toggle.setAttribute('aria-label', this.paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
        this.toggle.querySelector('[data-slideshow-toggle-icon]').textContent = this.paused ? '▶' : 'Ⅱ';
      }
      this.updateVideos();
    }
    updateVideos() {
      this.videos?.forEach(video => {
        if (!video.closest('[data-slide]').hidden && this.visible && !document.hidden && !this.motion.matches) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }
  });
}

if (!customElements.get('eh-product-grid')) {
  customElements.define('eh-product-grid', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.slots = [...this.querySelectorAll('.eh-products__slot')];
      this.slots.forEach(slot => {
        slot.addEventListener('pointerenter', event => {
          if (event.pointerType !== 'touch') this.activate(slot);
        }, options);
        slot.addEventListener('pointerleave', () => {
          clearTimeout(this.leaveTimer);
          this.leaveTimer = setTimeout(() => {
            this.activate(this.querySelector('.eh-products__slot:hover') || this.querySelector(':focus-visible')?.closest('.eh-products__slot'));
          }, 160);
        }, options);
      });
      this.addEventListener('focusin', event => this.activate(event.target.closest('.eh-products__slot')), options);
      this.addEventListener('focusout', event => this.activate(event.relatedTarget?.closest('.eh-products__slot') || this.querySelector('.eh-products__slot:hover')), options);
      this.addEventListener('shopify:block:select', event => this.activate(event.target.closest('.eh-products__slot')), options);
      this.resizeObserver?.disconnect();
      this.resizeObserver = new ResizeObserver(() => this.updateOutlines());
      this.slots.forEach(slot => this.resizeObserver.observe(slot.querySelector('.eh-products__card')));
      this.updateOutlines();
    }
    updateOutlines() {
      const scale = Math.min(1, Math.max(.65, this.clientWidth / 1632));
      // One size for the entire grid, independent of each card's aspect ratio.
      this.style.setProperty('--eh-bag-size', `${68 * scale}px`);
      this.style.setProperty('--eh-bag-left', `${25 * scale}px`);
      this.style.setProperty('--eh-bag-top', `${59 * scale}px`);
      this.style.setProperty('--eh-bag-overhang', `${60 * scale}px`);
      this.style.setProperty('--eh-bag-bottom', `${94 * scale}px`);
      this.slots.forEach(slot => {
        const card = slot.querySelector('.eh-products__card');
        const shape = card.querySelector('.eh-products__shape');
        const { width, height } = card.getBoundingClientRect();
        const right = width - .5;
        const bottom = height - .5;
        const x = offset => right + offset * scale;
        const y = offset => bottom + offset * scale;
        // Keep the card corners and bag circular across the two card proportions.
        shape.setAttribute('viewBox', `0 0 ${width + 60 * scale} ${height + 94 * scale}`);
        shape.style.width = `${width + 60 * scale}px`;
        shape.style.height = `${height + 94 * scale}px`;
        shape.querySelector('path').setAttribute('d', `M24.5 .5 H${right - 24}
          Q${right} .5 ${right} 24.5 V${y(6)}
          Q${right} ${y(20)} ${x(14)} ${y(20)} H${x(30)}
          Q${x(59)} ${y(20)} ${x(59)} ${y(49)} V${y(59)}
          A${34 * scale} ${34 * scale} 0 0 1 ${x(-9)} ${y(59)} V${y(33)}
          Q${x(-9)} ${y(28)} ${x(-11)} ${y(25)} L${x(-18)} ${y(13)}
          Q${x(-25)} ${bottom} ${x(-45)} ${bottom} H24.5
          Q.5 ${bottom} .5 ${bottom - 24} V24.5 Q.5 .5 24.5 .5 Z`);
      });
    }
    activate(active) {
      clearTimeout(this.leaveTimer);
      this.slots.forEach(slot => slot.toggleAttribute('data-active', slot === active));
    }
    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.leaveTimer);
      this.resizeObserver?.disconnect();
    }
  });
}
